/**
 * FriendsGroupsToggle — Friends | Groups using shared UnderlineTabBar (sound, badges, guest lock).
 */

import React, { useMemo } from 'react'
import { Users, LayoutGrid } from 'lucide-react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { useThemeContext } from '../../../src/theme/ThemeContext'
import {
  VIEW_TYPE,
  type ViewType,
} from '../../../src/friends/constants/viewFilter.constants'
import { GUEST_GROUPS_TAB_ACCESSIBILITY_LABEL } from '../../../src/shared/hooks/useGuestGuard.constants'
import { SOUND_IDS } from '../../../src/shared/constants/sounds.constants'
import { UnderlineTabBar, type UnderlineTabDef } from '../../ui/UnderlineTabBar'

const ICON_SIZE = 18

export interface FriendsGroupsToggleProps {
  viewType: ViewType
  onViewTypeChange: (viewType: ViewType) => void
  friendReceivedCount?: number
  groupReceivedCount?: number
  /** When true, Groups tab is non-interactive (e.g. guest mode). */
  groupsTabDisabled?: boolean
  testID?: string
}

export const FriendsGroupsToggle: React.FC<FriendsGroupsToggleProps> = ({
  viewType,
  onViewTypeChange,
  friendReceivedCount = 0,
  groupReceivedCount = 0,
  groupsTabDisabled = false,
  testID,
}) => {
  const theme = useTheme()
  const { themeMode } = useThemeContext()
  const isLight = themeMode === 'light'
  const activeTextColor = isLight ? theme.primary : theme.white

  const tabs = useMemo(
    (): readonly UnderlineTabDef<ViewType>[] => [
      {
        key: VIEW_TYPE.FRIENDS,
        label: 'Friends',
        Icon: Users,
        badgeCount: friendReceivedCount > 0 ? friendReceivedCount : undefined,
        testID: testID ? `${testID}-friends` : 'toggle-friends',
      },
      {
        key: VIEW_TYPE.GROUPS,
        label: 'Groups',
        Icon: LayoutGrid,
        badgeCount: groupReceivedCount > 0 ? groupReceivedCount : undefined,
        disabled: groupsTabDisabled,
        accessibilityLabel: groupsTabDisabled
          ? GUEST_GROUPS_TAB_ACCESSIBILITY_LABEL
          : undefined,
        testID: testID ? `${testID}-groups` : 'toggle-groups',
      },
    ],
    [
      friendReceivedCount,
      groupReceivedCount,
      groupsTabDisabled,
      testID,
    ],
  )

  return (
    <UnderlineTabBar<ViewType>
      tabs={tabs}
      activeTab={viewType}
      onTabChange={onViewTypeChange}
      activeTextColor={activeTextColor}
      inactiveTextColor={theme.gray[500]}
      underlineColor={theme.primary}
      iconSize={ICON_SIZE}
      includeBottomBorder
      bottomBorderColor={theme.gray[300]}
      labelFontSize={15}
      tabPaddingVertical={12}
      tabPaddingHorizontal={16}
      tabContentGap={8}
      tapSoundId={SOUND_IDS.UI_TAP_SOCIAL}
      containerTestID={testID ?? 'friends-groups-toggle'}
    />
  )
}
